var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.email.unsubscribe.page",
  title: "EGT Email Subscription Preferences page",
  description: "Branded, frosted-glass subscription preference centre. Enter an address, then turn individual newsletters on or off, or opt out of everything. No bare-GET side effect.",
  supportServer: true
});
var S = require("../helpers/egt.sections.js");

/* The client (/src/egt.unsubscribe.js) depends on these EXACT ids:
 *   #egt-unsub-lookup   (step 1: the address panel)
 *   #egt-unsub-prefs    (step 2: the newsletter panel, revealed after lookup)
 *   #egt-unsub-list     (where the newsletter toggles are rendered)
 *   #egt-unsub-confirm  (double opt-in: the confirmation-code panel, revealed
 *                        only when a TYPED-ADDRESS save adds a newsletter — the
 *                        server withholds that opt-in until the code is entered)
 *   #egt-unsub-confirm-btn  (submit the code)  + the code field name "code"
 *   #egt-unsub-done     (the success panel)
 *   #egt-unsub-status   (inline error/status)
 *   #egt-unsub-continue #egt-unsub-save #egt-unsub-all  (the three buttons)
 *   #egt-unsub-done-title #egt-unsub-done-copy          (success copy, swapped)
 *   the email field name "email".
 *
 * VOCABULARY: this surface says "newsletter" — never "segment". Segment is
 * admin-only language (see app/routes/egt.unsubscribe.js).
 *
 * The layout is a single calm frosted-glass card on the undimmed About canvas
 * image (house rule: never a scrim on the art — contrast comes from the glass).
 * Step 2 is populated by the client from the PUBLIC newsletter list, so the page
 * cache never hard-codes which lists exist.
 */

exports.render = async function render() {

  /* ---- STEP 1: whose preferences? -------------------------------------- */
  var lookupPanel = {
    n: "div", id: "egt-unsub-lookup", c: "egt-form egt-unsub__panel",
    i: [
      { n: "p", c: "egt__eyebrow egt-reveal", i: "Email preferences" },
      { n: "h1", c: "egt-unsub__title egt-reveal",
        i: [ "Manage your ", { n: "span", c: "egt__gold", i: "EGT" }, " newsletters" ] },
      { n: "p", c: "egt-unsub__lead egt-reveal",
        i: "Tell us which address this is and we'll show you the newsletters you're on. You can turn any of them on or off — or unsubscribe from everything." },
      { n: "div", c: "row egt-reveal", style: "--egt-i:1",
        i: { n: "div", c: "col-12", i: {
          n: "ua.input", title: "Your email address", icon: "bi-envelope", name: "email",
          class: "pt-3", required: true, autocomplete: "email",
          placeholder: "you@example.com", type: "email" } } },
      { n: "div", class: "pt-4 egt-reveal", style: "--egt-i:2",
        i: { n: "button", id: "egt-unsub-continue", type: "button", c: "egt-cta",
          i: [ "Show my newsletters", { n: "span", c: "egt-cta__tick", i: " ›" } ] } },
      { n: "p", c: "egt-unsub__fineprint egt-reveal", style: "--egt-i:3",
        i: "Nothing changes until you save. Opening this page on its own never unsubscribes you." }
    ]
  };

  /* ---- STEP 2: the newsletters ----------------------------------------- */
  var prefsPanel = {
    n: "div", id: "egt-unsub-prefs", c: "egt-unsub__panel d-none",
    i: [
      { n: "p", c: "egt__eyebrow", i: "Your newsletters" },
      { n: "h1", c: "egt-unsub__title",
        i: [ "Choose what you'd like to ", { n: "span", c: "egt__gold", i: "receive" } ] },
      { n: "p", c: "egt-unsub__lead",
        i: "Tick the newsletters you want, untick the ones you don't, then save. You'll still receive essential messages tied to anything you've purchased or requested." },

      // populated by the client from the PUBLIC newsletter list
      { n: "div", id: "egt-unsub-list", c: "egt-unsub__list", i: "" },

      { n: "div", class: "pt-4",
        i: { n: "button", id: "egt-unsub-save", type: "button", c: "egt-cta",
          i: [ "Save my preferences", { n: "span", c: "egt-cta__tick", i: " ›" } ] } },

      // CAN-SPAM: a simple, complete opt-out stays one obvious click away.
      { n: "div", c: "egt-unsub__all",
        i: [
          { n: "p", c: "egt-unsub__all-copy",
            i: "Would you rather not hear from us at all?" },
          { n: "button", id: "egt-unsub-all", type: "button", c: "egt-unsub__all-btn",
            i: "Unsubscribe from everything" }
        ] }
    ]
  };

  /* ---- CONFIRM: double opt-in code entry (typed-address opt-in) ----------
   * Revealed by the client only when a typed-address save ADDS a newsletter.
   * The server never writes that opt-in from a typed address until the owner
   * proves receipt with the six-digit code emailed to them — so a stranger
   * cannot subscription-bomb a victim onto our lists. Opt-outs in the same
   * save are already applied; only the additions wait for this code. */
  var confirmPanel = {
    n: "div", id: "egt-unsub-confirm", c: "egt-unsub__panel egt-unsub__confirm d-none",
    i: [
      { n: "p", c: "egt__eyebrow", i: "Confirm it's you" },
      { n: "h1", c: "egt-unsub__title",
        i: [ "Check your ", { n: "span", c: "egt__gold", i: "email" } ] },
      { n: "p", c: "egt-unsub__lead",
        i: "To protect your inbox, we only add a newsletter once you confirm it's yours. We've emailed you a six-digit code — enter it below to start receiving the newsletters you chose. Anything you turned off has already been saved." },
      { n: "div", c: "row",
        i: { n: "div", c: "col-12", i: {
          n: "ua.input", title: "Confirmation code", icon: "bi-shield-check", name: "code",
          class: "pt-3", inputmode: "numeric", autocomplete: "one-time-code",
          placeholder: "123456" } } },
      { n: "div", class: "pt-4",
        i: { n: "button", id: "egt-unsub-confirm-btn", type: "button", c: "egt-cta",
          i: [ "Confirm my newsletters", { n: "span", c: "egt-cta__tick", i: " ›" } ] } },
      { n: "p", c: "egt-unsub__fineprint",
        i: "The code is good for fifteen minutes. Didn't get it? Save your preferences again to send a new one." }
    ]
  };

  /* ---- The shared inline status region ---------------------------------- */
  var status = { n: "div", id: "egt-unsub-status", class: "egt-unsub__status d-none",
    role: "status", "aria-live": "polite", tabindex: "-1", i: "" };

  /* ---- The success state ------------------------------------------------
   * Server-rendered (not injected) so the confirmation is always fully branded
   * even before any script runs; the client swaps the title/copy for the
   * "saved" vs "unsubscribed from everything" wording. */
  var donePanel = {
    n: "div", id: "egt-unsub-done", c: "egt-unsub__panel egt-unsub__panel--done d-none",
    role: "status", "aria-live": "polite", tabindex: "-1",
    i: [
      { n: "div", c: "egt-unsub__check", "aria-hidden": "true",
        i: { n: "i", c: "bi-check-circle" } },
      { n: "p", c: "egt__eyebrow", i: "Done" },
      { n: "h1", id: "egt-unsub-done-title", c: "egt-unsub__title",
        i: [ "Your preferences are ", { n: "span", c: "egt__gold", i: "saved" }, "." ] },
      { n: "p", id: "egt-unsub-done-copy", c: "egt-unsub__lead",
        i: "Thanks — we've updated what you'll receive. It can take a moment to take effect across everything. You can come back to this page any time to change it again." },
      { n: "p", c: "egt-unsub__done-actions",
        i: { n: "a", href: "/", c: "egt-cta",
          i: [ "Back to egt.studio", { n: "span", c: "egt-cta__tick", i: " ›" } ] } }
    ]
  };

  var card = S.glass({
    width: "wide", tone: "dark", c: "egt-unsub__card egt-reveal",
    content: [ lookupPanel, prefsPanel, confirmPanel, status, donePanel ]
  });

  return S.page("/reserve", [
    { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-unsub",
      i: [
        S.media({ img: "/imgs/egt-about-canvas.jpg", alt: "", parallax: 0.08 }),
        { n: "div", c: "egt__shell egt-canvas__inner egt-unsub__inner",
          i: [ card ] }
      ] }
  ]);
};
